import React from "react";
import styled from "@emotion/styled";
import {useSelector} from "react-redux";
import {getActiveContest, getCurrentQuestion} from "modules/selectors/contest";
import {NullOrEmpty} from "modules/types";
import {getPlayerById} from "modules/selectors/JSON";
import {getDashedValue} from "modules/utils/helpers";
import {PlayerStat} from "components/PlayerStat";
import {useTranslation} from "react-i18next";
import {PlayerImage} from "components/PlayerImage";
import {PlayerImageType} from "modules/enums";
import {SquadImage} from "components/SquadImage";
import {ImageHandler} from "modules/utils/helpers/ImageHandler";

const CardBox = styled.div`
	max-width: 320px;
	min-height: 176px;
	width: 100%;
	height: auto;
	position: relative;
`;

const CardWrapper = styled.div<{background: string}>`
	width: 100%;
	position: absolute;
	max-width: 320px;
	min-height: 176px;
	-webkit-border-radius: 22px;
	-moz-border-radius: 22px;
	border-radius: 22px;
	background: url(${(props) => props.background});
	background-size: cover;
	box-shadow: 0 1px 2px rgba(28, 28, 30, 0.1), 0 8px 16px rgba(28, 28, 30, 0.2);
	transition-duration: 0.5s;
	z-index: 1;

	&:before {
		content: "";
		position: absolute;
		inset: 0;
		border: 2px solid transparent;
		-webkit-border-radius: 22px;
		-moz-border-radius: 22px;
		border-radius: 22px;
		transition: 0.5s;
		z-index: 22;
	}

	&.correct {
		&:before,
		hr {
			border-color: ${({theme}) => theme.playCard.correctColor};
		}
	}

	&.incorrect {
		&:before,
		hr {
			border-color: ${({theme}) => theme.playCard.incorrectColor};
		}
	}

	&.answered {
		transition-delay: 1s;
		z-index: 22;
		transform: translateY(-50%);
	}

	&.non-animated.previous {
		transition: 0.2s;
		opacity: 0;
	}

	&:after {
		content: "";
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		-webkit-border-radius: 22px;
		-moz-border-radius: 22px;
		border-radius: 22px;
		z-index: 2;
		background: linear-gradient(0, #000000 0%, #2c2c2e 100%);
		opacity: 0.9;
	}
`;

const ImageWrapper = styled.div`
	width: 100%;
	max-width: 50%;
	height: 100%;
	min-height: 161px;
	max-height: 165px;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;

	img {
		width: auto;
		height: 100%;
		max-width: 100%;
		-webkit-border-radius: 0 0 22px 0;
		-moz-border-radius: 0 0 22px 0;
		border-radius: 0 0 22px 0;
	}
`;

const CardContent = styled.div`
	position: relative;
	z-index: 5;
	width: 100%;

	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding: 15px 0 0 15px;
	gap: 24px;

	color: ${({theme}) => theme.colors.text.white};
`;

const InfoBlock = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 8px;
	width: 50%;

	hr {
		width: 100%;
		height: 0;
		margin: 0;
		border: none;
		border-bottom: 1px solid ${({theme}) => theme.colors.brand.primary};
		transition: 0.25s;
	}

	span {
		font-size: 14px;
		line-height: 20px;
		letter-spacing: 0.2px;

		&.stats {
			font-weight: 600;
			text-transform: capitalize;
			word-break: break-all;
		}

		&.stats-value {
			font-weight: 800;
			font-size: 40px;
			line-height: 38px;
		}
	}
`;

const TopBlock = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;

	span {
		font-weight: 600;
		font-size: 18px;
		line-height: 20px;
		letter-spacing: 0.2px;
	}
`;

const SquadImageWrapper = styled.div`
	img {
		width: 30px;
		height: 30px;
		-webkit-border-radius: 4px;
		-moz-border-radius: 4px;
		border-radius: 4px;
	}
`;

const getCardClasses = (isAnimated: boolean, guess: NullOrEmpty<boolean>) => {
	let className = "";
	if (!isAnimated || guess === null || guess === undefined) {
		return "non-animated";
	}

	if (guess) {
		className += "answered correct";
	} else {
		className += "answered incorrect";
	}
	return className;
};

interface IProps {
	isAnimated: boolean;
	playerId: number;
	className?: string;
}

export const PlayerCard: React.FC<IProps> = ({isAnimated, playerId, className = ""}) => {
	const {t} = useTranslation();
	const question = useSelector(getCurrentQuestion);
	const cardClassed = getCardClasses(isAnimated, question?.isCorrect);
	const player = useSelector(getPlayerById)(playerId);
	const contest = useSelector(getActiveContest);
	const category = contest?.category ?? "error";

	if (!player) {
		return null;
	}

	return (
		<CardBox className={`${cardClassed} ${className}`}>
			<CardWrapper
				className={`${cardClassed} ${className} player-card`}
				background={ImageHandler.getPlayerActionShotImage(player, true) as string}>
				<CardContent>
					<InfoBlock>
						<TopBlock>
							<span className="player-name">
								{getDashedValue(player?.firstName)} <br />{" "}
								{getDashedValue(player?.lastName)}
							</span>
							<SquadImageWrapper>
								<SquadImage squadId={player?.squadId} />
							</SquadImageWrapper>
						</TopBlock>

						<hr />
						<span className="stats">{t(`game.gameplay.${category}`)}</span>
						<PlayerStat playerId={playerId} />
					</InfoBlock>
					<ImageWrapper>
						<PlayerImage player={player} type={PlayerImageType.headshot} />
					</ImageWrapper>
				</CardContent>
			</CardWrapper>
		</CardBox>
	);
};
